import type React from 'react';
import {
  canonicalizeLocale,
  DEFAULT_CONTENT_LOCALE,
  getReadableTextColor,
  useTheme,
  type LocaleOption,
  type ThemeTokens,
} from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { GlobeIcon } from './GlobeIcon';

export interface LessonLanguageAvailability {
  selectedLocale?: string;
  defaultLocale?: string;
  fullyTranslatedLocales?: readonly string[];
  /** Compatibility fallback for lesson payloads created before completeness was packaged. */
  availableLocales?: readonly LocaleOption[];
}

export function shouldShowLessonLanguageFallback({
  selectedLocale,
  defaultLocale = DEFAULT_CONTENT_LOCALE,
  fullyTranslatedLocales,
  availableLocales,
}: LessonLanguageAvailability): boolean {
  const selected = canonicalizeLocale(selectedLocale);
  if (!selected) return false;

  const resolvedDefault = canonicalizeLocale(defaultLocale) || DEFAULT_CONTENT_LOCALE;
  if (selected === resolvedDefault) return false;

  if (fullyTranslatedLocales) {
    return !fullyTranslatedLocales.some((locale) => canonicalizeLocale(locale) === selected);
  }

  return !availableLocales?.some((option) => canonicalizeLocale(option.locale) === selected);
}

export interface LanguageUnavailableBannerProps {
  /**
   * i18n key for the message. Defaults to the lesson wording; other surfaces (e.g.
   * the questionnaire) pass their own so the noun matches what the learner is in.
   */
  messageKey?: string;
}

export function LanguageUnavailableBanner({
  messageKey = 'lesson_language_unavailable.message',
}: LanguageUnavailableBannerProps = {}): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const styles = createStyles(tokens);

  return (
    <div
      role="status"
      aria-live="polite"
      style={styles.banner}
      data-testid="language-unavailable-banner"
    >
      <GlobeIcon />
      <span style={styles.message}>
        {t(messageKey)}
      </span>
    </div>
  );
}

LanguageUnavailableBanner.displayName = 'LanguageUnavailableBanner';

function createStyles(
  tokens: ThemeTokens,
): Record<string, React.CSSProperties> {
  const bannerColor = tokens.ui.fixedAccent;

  return {
    banner: {
      width: '100%',
      backgroundColor: bannerColor,
      padding: '15px',
      borderRadius: '5px',
      boxShadow: '0px 4px 2px rgba(0,0,0,0.15)',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      color: getReadableTextColor(bannerColor),
      fontFamily: tokens.typography.fontFamily,
    },
    message: {
      flex: 1,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.bold,
      lineHeight: 'normal',
    },
  };
}
